import { $myClients, $myClientsSearchResult, $myClientsSearchValue } from '@/entities/my-clients-entity';
import { useUnit } from 'effector-react';
import MyClientCard from './my-client-card';
import { useMemo, useRef } from 'react';
import { $myFilter } from '@/entities/my-filter-entity';
import {
	clientHasNonEmptyPhoneNumber,
	clientPassesPhoneCountryExclusionForClient,
} from '@crave/config/phone-country';
import { useVirtualizer } from '@tanstack/react-virtual';

export default function MyClientsList() {
	const listRef = useRef<HTMLDivElement | null>(null);
	const [myClients, searchClients, searchValue] = useUnit([$myClients, $myClientsSearchResult, $myClientsSearchValue]);
	const filter = useUnit($myFilter);

	const filteredClients = useMemo(() => {
		return myClients.filter(client => {
			if (filter.onlineStatus === 'online' && !client.isOnline) return false;
			if (filter.onlineStatus === 'offline' && client.isOnline) return false;
			if (filter.removeWithoutPhone && !clientHasNonEmptyPhoneNumber(client?.phone, client.phones ?? [])) return false;
			if (
				filter.excludePhoneCountries.length &&
				!clientPassesPhoneCountryExclusionForClient(
					filter.excludePhoneCountries,
					client?.phone,
					client.phones ?? [],
				)
			) {
				return false;
			}
			if (filter.removeTgUsed && client.tgStatus === 'used') return false;
			if (filter.removeWithoutTgCode && client.tgStatus === 'woutcode') return false;
			if (filter.removeWithTg2FA && client.tgStatus === '2fa') return false;
			if (filter.removeTaken && client.takeStatus === 1) return false;
			return true;
		});
	}, [filter, myClients]);

	const clients = searchClients.length || searchValue.length ? searchClients : filteredClients;

	const virtualizer = useVirtualizer({
		count: clients.length,
		getScrollElement: () => listRef.current,
		estimateSize: () => 100,
		overscan: 5,
		measureElement: el => el.getBoundingClientRect().height,
		gap: 8,
	});

	if (!clients.length) {
		return (
			<div className='flex mt-[50px] text-md justify-center h-full'>
				{searchValue.length ? 'Ничего не найдено' : 'Нет клиентов'}
			</div>
		);
	}

	const virtualItems = virtualizer.getVirtualItems();

	return (
		<div ref={listRef} data-scroll-container className='h-full overflow-auto pr-1'>
			<div
				style={{
					height: `${virtualizer.getTotalSize()}px`,
					width: '100%',
					position: 'relative',
				}}
			>
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
					}}
				>
					{virtualItems.map(({ index, key }) => {
						const card = clients[index];
						return (
							<div key={key} data-index={index} ref={virtualizer.measureElement} className='pb-2'>
								<MyClientCard client={card} />
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
